import { Text } from 'app/design/primitives/text';
import { Column, Row } from 'app/design/primitives/box';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { UseFormReturn } from 'react-hook-form';
import { Controller } from 'react-hook-form';
import { Pressable } from 'react-native';
import { GeoPin } from 'app/design/icons/geo-pin';
import MapSheet from 'app/atoms/map-sheet';
import { createTheme as createDSTheme } from 'app/shopify';
import { useSearchCenter } from 'app/hooks/use-search-center';
import type { LatLng } from 'react-native-maps';
import { useDistanceUnit } from 'app/hooks/use-distance-unit';
import { IconChevronRight } from '@tabler/icons-react-native';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
const theme = createDSTheme();

export default function TravelDistanceSection({
  form,
  defaultToPreference = true,
}: Readonly<{
  form: UseFormReturn<any>;
  defaultToPreference?: boolean;
}>) {
  const [isMapSheetVisible, setIsMapSheetVisible] = useState(false);
  const { t } = useTranslation('travel-distance');

  const { convertDistance } = useDistanceUnit();
  const watchedDistance = form.watch('customDistance');
  const watchedCenterLatitude = form.watch('customCenterLatitude');
  const watchedCenterLongitude = form.watch('customCenterLongitude');

  const { searchCenter, searchDistance } = useSearchCenter();
  const displayedDistance =
    typeof watchedDistance === 'number' && watchedDistance > 0 ? watchedDistance : searchDistance;
  useEffect(() => {
    if (defaultToPreference) {
      if (searchDistance) {
        form.setValue('customDistance', searchDistance);
      }
      if (searchCenter.radiusCenterLatitude) {
        form.setValue('customCenterLatitude', searchCenter.radiusCenterLatitude);
        form.setValue('customCenterLongitude', searchCenter.radiusCenterLongitude);
      }
    }
  }, [defaultToPreference, form, searchCenter, searchDistance]);

  const onClose = () => {
    setIsMapSheetVisible(false);
  };

  return (
    <Controller
      {...{ control: form.control }}
      name="customDistance"
      render={({ field }) => {
        const onSave = (d?: number, center?: LatLng) => {
          field.onChange(d);

          if (center?.latitude) {
            form.setValue('customCenterLatitude', center.latitude);
            form.setValue('customCenterLongitude', center.longitude);
          }

          onClose();
        };

        return (
          <>
            <Column gap="spacing.general.x2">
              <Row gap="spacing.general.x1">
                <Text color="text.primary" fontWeight="700" variant="bodyBase">
                  {t('title')}
                </Text>
                <Text color="text.primary" variant="bodyBase">
                  ({t('mandatory', { ns: 'common' })})*
                </Text>
              </Row>
              <Pressable onPress={() => setIsMapSheetVisible(true)}>
                <Row
                  alignItems="center"
                  borderColor="border.secondary"
                  borderRadius="radius.lg"
                  borderWidth={1}
                  flex={1}
                  padding="spacing.general.x2"
                >
                  <GeoPin color={theme.colors['text.placeholder']} />
                  <Text flex={1} fontSize={16} paddingLeft="spacing.general.x2">
                    {convertDistance(displayedDistance, { withUnit: true })}
                  </Text>
                  <IconChevronRight strokeWidth={ICON_STROKE_WIDTH} color={theme.colors['text.placeholder']} />
                </Row>
              </Pressable>
            </Column>

            <MapSheet
              initialRadius={displayedDistance}
              initialRadiusCenter={{ latitude: watchedCenterLatitude, longitude: watchedCenterLongitude }}
              onClose={onClose}
              onSave={onSave}
              open={isMapSheetVisible}
              variant={{ audience: 'visible' }}
            />
          </>
        );
      }}
    />
  );
}
